/**
 * Bilder in einer fertigen Mail finden und eines austauschen.
 *
 * Getauscht wird nur die Adresse des Bildes im <img>-Tag — Größe, Alt-Text
 * und Umgebung bleiben, wie sie sind. Das neue Bild wird auf das
 * Seitenverhältnis des alten zugeschnitten, damit nichts verrutscht.
 */

export interface MailBild {
  /** Stelle des <img>-Tags im HTML */
  start: number;
  ende: number;
  src: string;
  alt: string;
  /** Maße laut Tag (so groß erscheint das Bild in der Mail) */
  breite: number | null;
  hoehe: number | null;
}

/** Logos und Symbole (schmaler als das) bleiben außen vor. */
const MIN_BREITE = 200;
/** Längste Seite der Ausgabe: Mailbreite in doppelter Auflösung. */
const AUSGABE_MAX = 1240;

function attr(tag: string, name: string): string | null {
  const m = new RegExp(`\\s${name}\\s*=\\s*"([^"]*)"`, 'i').exec(tag);
  return m ? m[1] : null;
}

function zahl(s: string | null): number | null {
  const n = s ? parseInt(s, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Alle Inhaltsbilder der Mail (Logos, Symbole und Tracking-Pixel nicht). */
export function mailBilder(html: string): MailBild[] {
  const liste: MailBild[] = [];
  const re = /<img\b[^>]*>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    const tag = m[0];
    const src = attr(tag, 'src');
    if (!src || !/^https?:\/\//i.test(src)) continue;
    const breite = zahl(attr(tag, 'width'));
    if (breite !== null && breite < MIN_BREITE) continue;
    liste.push({
      start: m.index, ende: m.index + tag.length, src, alt: attr(tag, 'alt') ?? '',
      breite, hoehe: zahl(attr(tag, 'height')),
    });
  }
  return liste;
}

/** Adresse des Bildes im Tag austauschen. Wirft, wenn sich das HTML inzwischen verschoben hat. */
export function bildErsetzen(html: string, bild: MailBild, neueSrc: string): string {
  const tag = html.slice(bild.start, bild.ende);
  if (!/^<img\b/i.test(tag) || attr(tag, 'src') !== bild.src) {
    throw new Error('Die Mail hat sich verändert. Bitte öffne den Dialog noch einmal.');
  }
  const neu = tag.replace(/(\ssrc\s*=\s*")[^"]*(")/i, (_t, a: string, b: string) => `${a}${neueSrc.replace(/"/g, '%22')}${b}`);
  return html.slice(0, bild.start) + neu + html.slice(bild.ende);
}

// ── Zuschnitt ──────────────────────────────────────────────────────────────

export const ZOOM_MAX = 4;

/** x, y = Mittelpunkt des Ausschnitts im Original (0 bis 1), zoom 1 = größter Ausschnitt */
export interface BildZuschnitt { x: number; y: number; zoom: number }

export function ausschnitt(bildB: number, bildH: number, verhaeltnis: number, z: BildZuschnitt): { sx: number; sy: number; sb: number; sh: number } {
  let sb = bildB;
  let sh = bildB / verhaeltnis;
  if (sh > bildH) { sh = bildH; sb = bildH * verhaeltnis; }
  const zoom = Math.min(ZOOM_MAX, Math.max(1, z.zoom || 1));
  sb /= zoom;
  sh /= zoom;
  const cx = Math.min(bildB - sb / 2, Math.max(sb / 2, (z.x ?? 0.5) * bildB));
  const cy = Math.min(bildH - sh / 2, Math.max(sh / 2, (z.y ?? 0.5) * bildH));
  return { sx: cx - sb / 2, sy: cy - sh / 2, sb, sh };
}

export function begrenzen(bildB: number, bildH: number, verhaeltnis: number, z: BildZuschnitt): BildZuschnitt {
  const a = ausschnitt(bildB, bildH, verhaeltnis, z);
  return { zoom: Math.min(ZOOM_MAX, Math.max(1, z.zoom || 1)), x: (a.sx + a.sb / 2) / bildB, y: (a.sy + a.sh / 2) / bildH };
}

/** Ausschnitt als fertiges JPEG zeichnen (doppelte Auflösung, höchstens 1240 px breit). */
export function zuschneiden(img: HTMLImageElement, verhaeltnis: number, z: BildZuschnitt, zielBreite: number): Promise<File> {
  const breite = Math.min(AUSGABE_MAX, Math.max(1, Math.round(zielBreite * 2)));
  const c = document.createElement('canvas');
  c.width = breite;
  c.height = Math.max(1, Math.round(breite / verhaeltnis));
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.imageSmoothingQuality = 'high';
  const a = ausschnitt(img.naturalWidth, img.naturalHeight, verhaeltnis, z);
  ctx.drawImage(img, a.sx, a.sy, a.sb, a.sh, 0, 0, c.width, c.height);
  return new Promise((resolve, reject) => {
    c.toBlob((b) => {
      if (!b) { reject(new Error('Das Bild ließ sich nicht umwandeln.')); return; }
      resolve(new File([b], `bild-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.86);
  });
}
